import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { DollarSign, Pencil, RefreshCw } from 'lucide-react';
import { toast } from '../../lib/safeToast';

export default function BackOfficeConfigFees() {
  const { api } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRule, setEditRule] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/fee-rules');
      setRules(res.data?.data || []);
    } catch { toast.error('Failed to load fee rules'); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const openEdit = (rule) => {
    setEditRule(rule);
    setEditForm({ name: rule.name, type: rule.type, value: rule.value, min: rule.min, max: rule.max, currency: rule.currency, active: rule.active });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/backoffice/fee-rules/${editRule.id}`, editForm);
      toast.success('Fee rule updated');
      setEditRule(null);
      fetchRules();
    } catch { toast.error('Failed to update rule'); } finally { setSaving(false); }
  };

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2"><DollarSign className="w-7 h-7 text-[#0B6445]" />Fee & Pricing Rules</h1>
            <p className="text-[#0E1B16]/70 mt-2">Manage transaction fee structures and pricing tiers.</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRules} className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="spinner w-8 h-8" /></div>
        ) : (
          <Card className="border-[#DCE4E0] shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-base text-[#073B2E]">{rules.length} Fee Rule(s)</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#DCE4E0] bg-[#F8FAF9]">
                    <th className="text-left py-3 px-4 font-semibold text-[#073B2E]">Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#073B2E]">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#073B2E]">Value</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#073B2E]">Min / Max</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#073B2E]">Currency</th>
                    <th className="text-center py-3 px-4 font-semibold text-[#073B2E]">Status</th>
                    <th className="text-right py-3 px-4 font-semibold text-[#073B2E]">Action</th>
                  </tr></thead>
                  <tbody>
                    {rules.map((r) => (
                      <tr key={r.id} className="border-b border-[#DCE4E0]/60 hover:bg-[#F8FAFC]">
                        <td className="py-3 px-4 font-medium">{r.name}</td>
                        <td className="py-3 px-4 capitalize">{r.type}</td>
                        <td className="py-3 px-4">{r.type === 'percentage' ? `${r.value}%` : `$${r.value}`}</td>
                        <td className="py-3 px-4 text-slate-500">${r.min} – ${r.max}</td>
                        <td className="py-3 px-4">{r.currency}</td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant="outline" className={r.active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-50 text-slate-500 border-slate-200'}>{r.active ? 'Active' : 'Disabled'}</Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={!!editRule} onOpenChange={() => setEditRule(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="text-[#0F172A]">Edit Fee Rule</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Name</Label><Input value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={editForm.type || 'percentage'} onValueChange={(v) => setEditForm({ ...editForm, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percentage">Percentage</SelectItem><SelectItem value="fixed">Fixed</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Value</Label><Input value={editForm.value || ''} onChange={(e) => setEditForm({ ...editForm, value: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Min</Label><Input value={editForm.min || ''} onChange={(e) => setEditForm({ ...editForm, min: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Max</Label><Input value={editForm.max || ''} onChange={(e) => setEditForm({ ...editForm, max: e.target.value })} /></div>
              </div>
              <div className="flex items-center gap-3">
                <Label className="mb-0">Active</Label>
                <button type="button" onClick={() => setEditForm({ ...editForm, active: !editForm.active })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editForm.active ? 'bg-[#0B6445]' : 'bg-slate-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editForm.active ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setEditRule(null)} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#0B6445] hover:bg-[#095b3d] text-white">{saving ? 'Saving…' : 'Save Changes'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </BackOfficeLayout>
  );
}
